import type {
  AggregatedColumnJSON,
  ApiDashboardElement,
  ApiReturnDashboard,
  ApiReturnMeasureType,
  ApiSourceLabelPair,
  EntityPropertyColumnJSON,
  JSONSerializable,
  ReportElementType,
  ReportLocalizationFromCoordinates,
  ReportMap,
  ReportPlot,
  ReportStructure,
  ReportTable,
  ReportTextFromEntityProperty,
  SelectionSchemaMeasurePoint,
  SourceMap,
} from '@highimpact-frontend/api-types';
import { plotPreferencesFromSources } from '@highimpact/scatterplot';
import {
  getSourceLabelFromMap,
  resolveSourcesFromMap,
  toSourceLabelKey,
} from '../api/sourceLabelKeys';
import * as A from '../internal/utils/array';
import { identity } from '../internal/utils/functions';
import * as R from '../internal/utils/record';
import * as S from '../internal/utils/set';
import {
  makeEmptyCollection,
  makeEmptyImage,
  makeEmptyImageGallery,
  makeEmptyLocalizationFromCoordinates,
  makeEmptyMap,
  makeEmptyPlot,
  makeEmptyRemoteWidget,
  makeEmptyTable,
  makeEmptyTableByCampaign,
  makeEmptyText,
  makeEmptyTextFromEntityProperty,
  makeImageWithResource,
  makeSetDataSourcesFromLabelMap,
  MeasureTypeIdToUnit,
  setAdditionalProperties,
  setElements,
  setIntervals,
  setLayout,
  setName,
  setTextValue,
} from './dashboard-element-operations';
import type { DashboardGridType } from './dashboard/initials';
import {
  isPathWithMeasurePoint,
  isPathWithProjectSite,
} from './sourcePathGuards';
import {
  makeAggregatedColumn,
  makeEntityPropertyColumn,
  setAggregationTableColumnsE,
} from './table-element-operations';

// -- Transformations

export const reportAggregationNameToDashboardAggregationName = (
  aggregation: ReportTable['columns'][number]['transformation_type']
): AggregatedColumnJSON['transformation_type'] => {
  switch (aggregation) {
    case 'average':
      return 'mean';
    case 'count':
      return 'count';
    case 'lower_quartile':
      return 'first_quartile';
    case 'max':
      return 'max';
    case 'median':
      return 'median';
    case 'min':
      return 'min';
    case 'sum':
      return 'sum';
    case 'upper_quartile':
      return 'third_quartile';
    default:
      throw new Error(`Unhandled aggregation type: ${aggregation}`);
  }
};

// -- Type refinement

type WidthDataSourcesIndex =
  | ReportPlot
  | ReportTable
  | ReportMap
  | ReportTextFromEntityProperty
  | ReportLocalizationFromCoordinates;

type WithTimeRangeIndex = WidthDataSourcesIndex;

export const isWithDataSourcesIndex = (
  element: ReportElementType
): element is WidthDataSourcesIndex => {
  return (
    element.element_type === 'plot' ||
    element.element_type === 'table' ||
    element.element_type === 'tablebycampaign' ||
    element.element_type === 'map' ||
    element.element_type === 'textfromentityproperty' ||
    element.element_type === 'image' ||
    element.element_type === 'localizationfromcoordinates'
  );
};

export const isWithTimeRangeIndex = (
  element: ReportElementType
): element is WithTimeRangeIndex => {
  return (
    element.element_type === 'plot' ||
    element.element_type === 'tablebycampaign' ||
    element.element_type === 'table'
  );
};

export const isReportPlot = (
  element: ReportElementType
): element is ReportPlot => {
  return element.element_type === 'plot';
};

export const isReportTable = (
  element: ReportElementType
): element is ReportTable => {
  return element.element_type === 'table';
};

export const isReportTextFromEntityProperty = (
  element: ReportElementType
): element is ReportTextFromEntityProperty => {
  return element.element_type === 'textfromentityproperty';
};

// -- Getters

export const getTemplateVariableNames = (
  reportStructure: ReportStructure
): string[] => {
  return reportStructure.main_template.elements.map(
    (element) => element.template_variable
  );
};

const flattenElementsHelper = (
  acc: ReportElementType[],
  element: ReportElementType
): typeof acc => {
  if (element.element_type === 'collection') {
    const subElements = element.elements.flatMap((subElement) =>
      flattenElementsHelper([], subElement)
    );
    return acc.concat(element, subElements);
  }
  return acc.concat(element);
};

const flattenElements = (
  elements: ReportElementType[]
): ReportElementType[] => {
  return elements.flatMap((element) => flattenElementsHelper([], element));
};

export const getTemplateVariables = (
  reportStructure: ReportStructure
): ReportElementType[] => {
  return reportStructure.main_template.elements;
};

export const getFlattenedTemplateVariables = (
  reportStructure: ReportStructure
): ReportElementType[] => {
  return flattenElements(getTemplateVariables(reportStructure));
};

export const findTemplateVariableByName = (
  templateVariableName: string,
  reportStructure: ReportStructure
): ReportElementType | undefined => {
  return getTemplateVariables(reportStructure).find(
    (element) => element.template_variable === templateVariableName
  );
};

export const getDataSources = (reportStructure: ReportStructure) => {
  return reportStructure.selections;
};

export const getTimeRanges = (reportStructure: ReportStructure) => {
  return reportStructure.time_intervals;
};

export const getIndexedDataSources = (
  element: WidthDataSourcesIndex,
  reportStructure: ReportStructure
) => {
  const uniqueIndices = new Set(
    element.data_source.map((dataSource) => dataSource.selection_index)
  );
  const dataSources = getDataSources(reportStructure);
  const indexToDataSource = S.toArray(uniqueIndices).map(
    (index) => [index, dataSources[index]] as const
  );
  const dataSourceSortedByIndex = indexToDataSource
    .slice()
    .sort(([index1], [index2]) => index1 - index2)
    .map(([, dataSource]) => dataSource)
    .filter((dataSource) => dataSource !== undefined);
  return dataSourceSortedByIndex;
};

export const getIndexedTimeRanges = (
  element: WithTimeRangeIndex,
  reportStructure: ReportStructure
) => {
  const uniqueIndices = new Set(
    element.data_source
      .map((dataSource) => dataSource.time_interval_index)
      .filter((index) => index === 0 || Boolean(index))
  );
  const timeRanges = getTimeRanges(reportStructure);
  const indexToRange = S.toArray(uniqueIndices).map(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (index) => [index, timeRanges[index!]] as const
  );
  const rangesSortedByIndex = indexToRange
    .slice()
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    .sort(([index1], [index2]) => index1! - index2!)
    .map(([, range]) => range)
    .filter((range) => range !== undefined);
  return rangesSortedByIndex.map((range) => ({
    start_date: range.start,
    end_date: range.end,
  }));
};

// -- Label / unit helpers

const dedupeSourceLabelPairs = (
  pairs: readonly ApiSourceLabelPair[]
): ApiSourceLabelPair[] => {
  const seen = new Set<string>();

  return pairs.filter((pair) => {
    const key = toSourceLabelKey(pair.measure_point_id, pair.measure_type_id);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/** Collects unique measure-point pairs referenced by a report structure. */
export const collectSourceLabelPairsFromReportStructure = (
  reportStructure: ReportStructure
): ApiSourceLabelPair[] => {
  const pairs = reportStructure.selections
    .filter(isPathWithMeasurePoint)
    .map(({ measure_point_id, measure_type_id }) => ({
      measure_point_id,
      measure_type_id,
    }));

  return dedupeSourceLabelPairs(pairs);
};

/** Builds measure-type → unit mapping from catalog measure types (no association graph). */
export const buildMeasureTypeIdToUnitFromMeasureTypes = (
  paths: readonly SelectionSchemaMeasurePoint[],
  measureTypes: readonly ApiReturnMeasureType[]
): MeasureTypeIdToUnit => {
  const measureTypeById = Object.fromEntries(
    measureTypes.map((measureType) => [measureType.id, measureType])
  );
  const uniqueMeasureTypeIds = [
    ...new Set(paths.map((path) => path.measure_type_id)),
  ];

  return Object.fromEntries(
    uniqueMeasureTypeIds.map((measureTypeId) => [
      measureTypeId,
      measureTypeById[measureTypeId]?.unite ?? '-',
    ])
  );
};

const resolvePathLabel = (
  sourceMap: SourceMap,
  path: SelectionSchemaMeasurePoint
): string =>
  getSourceLabelFromMap(sourceMap, path) ??
  `Point ${path.measure_point_id} (${path.measure_type_id})`;

// -- Constructors

export const buildMapping = (
  reportStructure: ReportStructure
): Record<string, string> => {
  return R.fromArray(
    getFlattenedTemplateVariables(reportStructure).map(
      ({ name, template_variable }) => [template_variable, name]
    )
  );
};

const makeDashboardTableElementFromReportTableElement = (
  sourceMap: SourceMap,
  mapping: Record<string, string>,
  startDate: string,
  endDate: string,
  dataSources: SelectionSchemaMeasurePoint[],
  setDataSources: ReturnType<typeof makeSetDataSourcesFromLabelMap>,
  reportTable: ReportTable
): ApiDashboardElement => {
  const pathNames = dataSources.map((path) =>
    resolvePathLabel(sourceMap, path)
  );
  const aggregations = A.unique(
    reportTable.columns.map((col) => col.transformation_type)
  );
  const rows = dataSources.map((_, index) =>
    [
      makeEntityPropertyColumn('measure_point', index, 'name') as
        | EntityPropertyColumnJSON
        | AggregatedColumnJSON,
    ].concat(
      aggregations.map((_, aggIndex) =>
        makeAggregatedColumn(
          reportAggregationNameToDashboardAggregationName(
            aggregations[aggIndex]!
          ),
          index,
          pathNames[index]!
        )
      )
    )
  );
  return setAggregationTableColumnsE(
    setDataSources(
      dataSources,
      setName(
        mapping[reportTable.template_variable]!,
        makeEmptyTable({
          start_date: startDate,
          end_date: endDate,
        })
      )
    ),
    rows
  );
};

const makeDashboardElementFromReportElement = (
  sourceMap: SourceMap,
  layout: DashboardGridType['layouts']['lg'],
  mtid2unit: MeasureTypeIdToUnit,
  mapping: Record<string, string>,
  reportElement: ReportElementType,
  reportStructure: ReportStructure,
  recursionCount = 0
): ApiDashboardElement | null => {
  const dataSources = isWithDataSourcesIndex(reportElement)
    ? getIndexedDataSources(reportElement, reportStructure)
    : [];
  const indexedTimeRanges = isWithTimeRangeIndex(reportElement)
    ? getIndexedTimeRanges(reportElement, reportStructure)
    : [{ start_date: '', end_date: '' }];
  const { start_date, end_date } = indexedTimeRanges[0] ?? {
    start_date: '',
    end_date: '',
  };
  const setDataSources = makeSetDataSourcesFromLabelMap(
    mtid2unit,
    sourceMap
  );
  const elementLayout = layout.find(
    ({ i }) => i === reportElement.template_variable
  );
  const updateLayout =
    elementLayout !== undefined
      ? (element: ApiDashboardElement) => setLayout(elementLayout, element)
      : identity;
  switch (reportElement.element_type) {
    case 'plot': {
      const emptyPlot =
        reportElement.intervals.length > 0
          ? setIntervals(
              reportElement.intervals.map((i) => ({
                ...i,
                campaign_ids: null,
              })),
              makeEmptyPlot({
                plot_type: reportElement.plot_type,
                start_date: start_date,
                end_date: end_date,
              })
            )
          : makeEmptyPlot({
              plot_type: reportElement.plot_type,
              start_date: start_date,
              end_date: end_date,
            });
      const namedPlot = setName(
        mapping[reportElement.template_variable]!,
        emptyPlot
      );
      const plotWithDataSources = setDataSources(dataSources, namedPlot);
      const measurePointPaths = dataSources.filter(isPathWithMeasurePoint);

      if (
        (reportElement.plot_type === 'timeseries' ||
          reportElement.plot_type === 'scatter') &&
        measurePointPaths.length > 0
      ) {
        const sources = resolveSourcesFromMap(measurePointPaths, sourceMap);

        if (sources.length > 0) {
          const plotPreferences = plotPreferencesFromSources(
            sources,
            reportElement.plot_type === 'scatter'
          );

          return updateLayout(
            setAdditionalProperties(
              plotPreferences as unknown as JSONSerializable,
              plotWithDataSources
            )
          );
        }
      }

      return updateLayout(plotWithDataSources);
    }
    case 'table':
      return updateLayout(
        setIntervals(
          reportElement.intervals.map((i) => ({
            ...i,
            campaign_ids: null,
          })) ?? [],
          makeDashboardTableElementFromReportTableElement(
            sourceMap,
            mapping,
            start_date,
            end_date,
            dataSources.filter(isPathWithMeasurePoint),
            setDataSources,
            reportElement
          )
        )
      );
    case 'tablebycampaign':
      return updateLayout(
        setDataSources(
          dataSources,
          setName(
            mapping[reportElement.template_variable]!,
            makeEmptyTableByCampaign({
              start_date: start_date,
              end_date: end_date,
            })
          )
        )
      );
    case 'map':
      return updateLayout(
        setDataSources(
          dataSources,
          setName(
            mapping[reportElement.template_variable]!,
            makeEmptyMap('site')
          )
        )
      );
    case 'text':
      return updateLayout(
        setDataSources(
          dataSources,
          setTextValue(
            reportElement.value ?? '',
            setName(mapping[reportElement.template_variable]!, makeEmptyText())
          )
        )
      );
    case 'textfromentityproperty':
      return updateLayout(
        setDataSources(
          dataSources,
          setName(
            mapping[reportElement.template_variable]!,
            setTextValue(
              reportElement.value ?? '',
              makeEmptyTextFromEntityProperty({
                // FIXME: fix entity_type on the report api
                entity_type: reportElement.entity_type as 'site' | 'project',
                entity_id: dataSources.find(isPathWithProjectSite)![
                  `${reportElement.entity_type as 'site' | 'project'}_id`
                ],
                entity_property: reportElement.entity_property,
              })
            )
          )
        )
      );
    case 'image':
      return updateLayout(
        setDataSources(
          dataSources,
          setName(
            mapping[reportElement.template_variable]!,
            // if we are inside a collection (recursionCount > 0) what we want is a collection of images
            // in the report and therefore an image gallery widget in the dashboard
            recursionCount === 0
              ? (reportElement.resource_ids?.length ?? 0) > 0
                ? makeImageWithResource(reportElement.resource_ids![0])
                : makeEmptyImage()
              : makeEmptyImageGallery()
          )
        )
      );
    case 'localizationfromcoordinates':
      return updateLayout(
        setDataSources(
          dataSources,
          setName(
            mapping[reportElement.template_variable]!,
            makeEmptyLocalizationFromCoordinates({
              coordinate_source: 'site',
            })
          )
        )
      );
    case 'remote':
      return updateLayout(
        setDataSources(
          dataSources,
          setAdditionalProperties(
            reportElement.additional_properties as JSONSerializable,
            setName(
              mapping[reportElement.template_variable]!,
              makeEmptyRemoteWidget(
                reportElement.specific_widget_type,
                reportElement.url
              )
            )
          )
        )
      );
    case 'collection':
      return setElements(
        reportElement.elements
          .map((element) =>
            makeDashboardElementFromReportElement(
              sourceMap,
              layout,
              mtid2unit,
              mapping,
              element,
              reportStructure,
              recursionCount + 1
            )
          )
          .filter((element) => element !== null),
        setName(
          mapping[reportElement.template_variable]!,
          makeEmptyCollection()
        )
      );
    // TODO: use correct widget representation
    case 'readonlytext':
      return updateLayout(
        setDataSources(
          dataSources,
          setTextValue(
            reportElement.value ?? '',
            setName(mapping[reportElement.template_variable]!, makeEmptyText())
          )
        )
      );
    // Non-widget elements
    case 'calculation':
      return null;
  }
};

export const buildDashboardStructureFromReportStructure = (
  sourceMap: SourceMap,
  layout: DashboardGridType['layouts']['lg'],
  mtid2unit: MeasureTypeIdToUnit,
  dashboardName: string,
  reportStructure: ReportStructure
): [Record<string, string>, ApiReturnDashboard] => {
  const mapping = buildMapping(reportStructure);
  const templateVariables = getTemplateVariables(reportStructure);
  return [
    mapping,
    {
      id: 0,
      name: dashboardName,
      elements: templateVariables
        .map((element) =>
          makeDashboardElementFromReportElement(
            sourceMap,
            layout,
            mtid2unit,
            mapping,
            element,
            reportStructure
          )
        )
        .filter((element) => element !== null),
      shared: false,
      type: 'report_dashboard',
      // FIXME: the backend should type the report config correctly instead of forcing the frontend to use this hack
      report_config: reportStructure as unknown as Record<string, never>,
      masked: false,
      additional_properties: {
        cols: { lg: 24, md: 18, sm: 12, xs: 9, xxs: 6 },
        rowHeight: 16,
        margin: [6, 6],
      },
    },
  ];
};
